import { useCallback, useEffect, useRef } from 'react'
import {
  useDashboardStore,
  selectMessages,
  selectStatus,
  selectCurrentInterrupt,
  selectIsApprovalPending,
  selectEnterpriseStatus,
  selectIsFallbackMode,
  selectFallbackReason,
  selectIsOrchestratorMode,
} from '@/store/dashboardStore'
import { useBlockStream } from '@/hooks/useBlockStream'
import { useOrchestratorStream } from '@/hooks/useOrchestratorStream'
import { useConversations } from '@/hooks/useConversations'
import { ChatMessageList } from './ChatMessageList'
import { ChatInput } from './ChatInput'
import { ApprovalModal } from '@/components/ApprovalModal'
import { EnterpriseStatusBanner } from '@/components/EnterpriseStatusBanner'
import { ORCHESTRATOR_AGENT_ID } from '@/types/orchestrator'

interface ChatViewProps {
  agentId: string
  agentName?: string
  conversationId?: string | null
  onConversationTitleUpdate?: (conversationId: string, title: string) => void
}

/**
 * Main chat container.
 * Wires streaming hooks, store, messages, input, and HITL approval.
 * When a conversationId is provided, persists messages to the backend.
 */
export function ChatView({ agentId, conversationId, onConversationTitleUpdate }: ChatViewProps) {
  const isOrchestratorAgent = agentId.toLowerCase() === ORCHESTRATOR_AGENT_ID.toLowerCase()

  // Store state
  const messages = useDashboardStore(selectMessages)
  const status = useDashboardStore(selectStatus)
  const currentInterrupt = useDashboardStore(selectCurrentInterrupt)
  const isApprovalPending = useDashboardStore(selectIsApprovalPending)
  const enterpriseStatus = useDashboardStore(selectEnterpriseStatus)
  const isFallbackMode = useDashboardStore(selectIsFallbackMode)
  const fallbackReason = useDashboardStore(selectFallbackReason)
  const isOrchestratorMode = useDashboardStore(selectIsOrchestratorMode)

  // Store actions
  const { addUserMessage, addAssistantMessage, finalizeCurrentMessage } = useDashboardStore()

  // Conversation persistence
  const { appendMessage, updateConversation } = useConversations()

  // Track the most recent user query so we can persist it after streaming completes
  const pendingUserQueryRef = useRef<string | null>(null)
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  // Streaming hooks
  const blockStream = useBlockStream({ agentId })
  const orchestratorStream = useOrchestratorStream()
  const activeStream = isOrchestratorAgent ? orchestratorStream : blockStream
  const { startStream, stopStream, isStreaming } = activeStream

  // Finalize current message when streaming completes, then persist to DB
  const prevStatusRef = useRef(status)
  useEffect(() => {
    const wasStreaming = prevStatusRef.current === 'streaming' || prevStatusRef.current === 'paused'
    const isDone = status === 'completed' || status === 'failed' || status === 'partial'
    if (wasStreaming && isDone) {
      finalizeCurrentMessage()

      // Persist to DB asynchronously (non-blocking)
      const convId = conversationIdRef.current
      if (convId) {
        const pendingQuery = pendingUserQueryRef.current
        pendingUserQueryRef.current = null

        // Persist user message
        if (pendingQuery) {
          appendMessage(convId, 'user', { query: pendingQuery }).catch(() => {})
        }

        // Persist assistant message (grab after finalize via timeout — state will be updated)
        setTimeout(() => {
          const updatedMessages = useDashboardStore.getState().messages
          const assistantMsg = updatedMessages[updatedMessages.length - 1]
          if (assistantMsg?.role === 'assistant') {
            appendMessage(convId, 'assistant', {
              title: assistantMsg.title,
              summary: assistantMsg.summary,
              blocks: assistantMsg.blocks,
              nodeExecutions: assistantMsg.nodeExecutions,
              status: assistantMsg.status,
              error: assistantMsg.error,
              traceId: assistantMsg.traceId,
              durationMs: assistantMsg.durationMs,
            }).catch(() => {})
          }

          // Auto-set title from first user message if still "New conversation"
          if (pendingQuery && onConversationTitleUpdate) {
            const allMessages = useDashboardStore.getState().messages
            // Only auto-title on the first exchange (2 messages: user + assistant)
            if (allMessages.length <= 2) {
              const title = pendingQuery.slice(0, 60)
              updateConversation(convId, { title }).then((updated) => {
                onConversationTitleUpdate(convId, updated.title)
              }).catch(() => {})
            }
          }
        }, 50)
      }
    }
    prevStatusRef.current = status
  }, [status, finalizeCurrentMessage, appendMessage, updateConversation, onConversationTitleUpdate])

  // Handle query submission
  const handleSubmit = useCallback(
    (query: string) => {
      pendingUserQueryRef.current = query

      addUserMessage(query)
      addAssistantMessage()

      startStream(query)
    },
    [addUserMessage, addAssistantMessage, startStream]
  )

  // Handle stop
  const handleStop = useCallback(() => {
    stopStream()
  }, [stopStream])

  // Handle HITL approval decision
  const handleApprovalDecision = useCallback(
    async (decision: Parameters<typeof orchestratorStream.resumeWorkflow>[0]) => {
      await orchestratorStream.resumeWorkflow(decision)
    },
    [orchestratorStream]
  )

  return (
    <div className="flex flex-col h-full">
      {/* HITL Approval Modal */}
      {currentInterrupt && (
        <ApprovalModal
          interrupt={currentInterrupt}
          onApprove={handleApprovalDecision}
          isProcessing={isApprovalPending}
        />
      )}

      {/* Enterprise Status Banner (only in orchestrator mode) */}
      {isOrchestratorMode && (
        <EnterpriseStatusBanner
          status={enterpriseStatus}
          isFallbackMode={isFallbackMode}
          fallbackReason={fallbackReason}
          className="mx-4 mt-2"
        />
      )}

      {/* Messages area */}
      <ChatMessageList messages={messages} />

      {/* Bottom input area */}
      <ChatInput
        onSubmit={handleSubmit}
        onStop={handleStop}
        isStreaming={isStreaming}
      />
    </div>
  )
}
